import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { uploadFile, type MediaPurpose } from '../api/mediaApi';
import type { CampaignAssetAttachment } from '@/modules/campaigns/api/campaignsApi';
import styles from './AssetSlotUploader.module.css';

function InlineDescription({ value, onCommit }: { value: string; onCommit: (v: string) => void }) {
  const [draft, setDraft] = useState(value);
  const editingRef = useRef(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (!editingRef.current) setDraft(value);
  }, [value]);

  useEffect(() => {
    autoResize();
  }, [draft]);

  return (
    <textarea
      ref={taRef}
      className={styles.tileDescEditable}
      value={draft}
      rows={1}
      onFocus={() => {
        editingRef.current = true;
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        autoResize();
      }}
      onBlur={() => {
        editingRef.current = false;
        const trimmed = draft.trim() || value;
        setDraft(trimmed);
        if (trimmed !== value) onCommit(trimmed);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setDraft(value);
          (e.target as HTMLTextAreaElement).blur();
        }
      }}
    />
  );
}

export interface SlotSpec {
  /** Backend role string, e.g. "square_marketing_image" */
  role: string;
  /** Human label, e.g. "Square" */
  label: string;
  /** Optional description from AI imageSuggestions */
  description?: string;
  /** Optional aspect ratio hint, e.g. "1:1" */
  aspectRatio?: string;
}

interface Props {
  locationId: string;
  slot: SlotSpec;
  attachment?: CampaignAssetAttachment;
  purpose?: MediaPurpose;
  disabled?: boolean;
  onAttach: (mediaAssetId: string, role: string) => Promise<void>;
  onDetach: (mediaAssetId: string, role: string) => Promise<void>;
  /** When provided, the slot description becomes inline-editable. */
  onRenameDescription?: (v: string) => void;
}

export function AssetSlotUploader({
  locationId,
  slot,
  attachment,
  purpose = 'creative',
  disabled,
  onAttach,
  onDetach,
  onRenameDescription,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setDragging] = useState(false);
  const [isUploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files[0]) return;
    setError(null);
    setUploading(true);
    try {
      const asset = await uploadFile({ locationId, file: files[0], purpose });
      await onAttach(asset.id, slot.role);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setError(msg);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (disabled || isUploading) return;
    void handleFiles(e.dataTransfer.files);
  };

  const handleRemove = async () => {
    if (!attachment) return;
    setError(null);
    try {
      await onDetach(attachment.mediaAssetId, attachment.role);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Detach failed');
    }
  };

  const dropzoneClass = [
    styles.dropzone,
    isDragging ? styles.dropzoneActive : '',
    disabled || isUploading ? styles.dropzoneDisabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.tile}>
      <div className={styles.tileLabel}>
        <span className={styles.tileSlot}>{slot.label}</span>
        {slot.aspectRatio && <span className={styles.tileRatio}>{slot.aspectRatio}</span>}
      </div>
      {slot.description &&
        (onRenameDescription ? (
          <InlineDescription value={slot.description} onCommit={onRenameDescription} />
        ) : (
          <div className={styles.tileDesc}>{slot.description}</div>
        ))}

      {attachment ? (
        <>
          <button
            type="button"
            className={styles.preview}
            onClick={() => setPreviewOpen(true)}
            aria-label={`View ${attachment.mediaAsset.originalFilename} full size`}
          >
            <img
              src={attachment.mediaAsset.url}
              alt={attachment.mediaAsset.originalFilename}
              className={styles.previewImg}
            />
          </button>
          <div className={styles.previewMeta}>
            <span>
              {attachment.mediaAsset.width}×{attachment.mediaAsset.height}
              {' · '}
              {(attachment.mediaAsset.sizeBytes / 1024).toFixed(0)} KB
            </span>
            <button
              type="button"
              className={styles.removeBtn}
              onClick={handleRemove}
              disabled={disabled}
            >
              Remove
            </button>
          </div>
          {previewOpen && (
            <Lightbox asset={attachment.mediaAsset} onClose={() => setPreviewOpen(false)} />
          )}
        </>
      ) : (
        <button
          type="button"
          className={dropzoneClass}
          onClick={() => !disabled && !isUploading && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !isUploading) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          disabled={disabled || isUploading}
        >
          <span className={styles.dropzoneIcon}>＋</span>
          <span>{isUploading ? 'Uploading…' : 'Drop image or click to upload'}</span>
          <span className={styles.dropzoneHint}>JPG · PNG · WebP · max 10MB</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

function Lightbox({
  asset,
  onClose,
}: {
  asset: CampaignAssetAttachment['mediaAsset'];
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className={styles.lightboxOverlay} onClick={onClose} role="dialog" aria-modal>
      <button type="button" className={styles.lightboxClose} onClick={onClose} aria-label="Close">
        <X size={16} aria-hidden />
      </button>
      <figure className={styles.lightboxFigure} onClick={(e) => e.stopPropagation()}>
        <img src={asset.url} alt={asset.originalFilename} className={styles.lightboxImg} />
        <figcaption className={styles.lightboxCaption}>
          <span className={styles.lightboxName}>{asset.originalFilename}</span>
          <span className={styles.lightboxMeta}>
            {asset.width}×{asset.height} · {(asset.sizeBytes / 1024).toFixed(0)} KB ·{' '}
            {asset.mimeType}
          </span>
        </figcaption>
      </figure>
    </div>
  );
}
